import { PropsWithChildren } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { DialogOverlay, DialogContent } from '@reach/dialog'

import styles from './Dialog.module.css'
import { XIcon } from '@heroicons/react/outline'

export interface DialogProps {
  isOpen: boolean
  width:
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl'
  hideHeader?: boolean
  showCloseText?: boolean
  title?: string
  subtitle?: any
  handleClose: () => void
}

const AnimatedOverlay = motion.custom(DialogOverlay)
const AnimatedContent = motion.custom(DialogContent)

const overlayVariants = {
  hidden: {
    opacity: 0,
    transition: {
      duration: 0.1,
      when: 'afterChildren'
    }
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.1,
      when: 'beforeChildren'
    }
  }
}

const contentVariants = {
  hidden: {
    transition: { duration: 0.1 },
    opacity: 0,
    y: 10
  },
  visible: {
    transition: { duration: 0.1 },
    opacity: 1,
    y: 0
  }
}

export function Dialog(props: PropsWithChildren<DialogProps>) {
  return (
    <AnimatePresence>
      {props.isOpen && (
        <AnimatedOverlay
          isOpen={true}
          onDismiss={props.handleClose}
          className={styles.overlay + ' max-h-screen transform-gpu z-10'}
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          <AnimatedContent
            aria-label="Log In to your Majordomo Account"
            className={`relative ${styles.content} max-w-${props.width} transform-gpu`}
            variants={contentVariants}
          >
            {/* Hidden input to prevent Reach Dialog from auto-focusing select  */}
            <input
              type="text"
              className="absolute top-0 opacity-0 h-0"
              readOnly
            />

            {!props.hideHeader && (
              <div className="flex items-center justify-between rounded-t bg-gray-100 border-b border-gray-300 md:px-8 px-4 py-4 sticky top-0 z-10">
                {props.title && (
                  <div>
                    <h2 className="flex items-center font-black text-brand font-heading md:text-xl text-lg max-w-xl">
                      {props.title}
                    </h2>

                    {props.subtitle}
                  </div>
                )}
                <div
                  data-testid="closeDialog"
                  onClick={props.handleClose}
                  className="ml-auto"
                >
                  {props.showCloseText ? (
                    <p className="font-heading text-accentBlue hover:underline cursor-pointer text-sm">
                      Save and Close
                    </p>
                  ) : (
                    <div onClick={props.handleClose}>
                      <XIcon
                        width={28}
                        height={28}
                        className="text-black transition hover:text-black cursor-pointer"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {props.children}
          </AnimatedContent>
        </AnimatedOverlay>
      )}
    </AnimatePresence>
  )
}
